import { useEffect, useMemo, useState } from "react";
import FilterBy from "../../../common/components/filter-by/FilterBy";
import GridSearchBar from "../../../common/components/grid-search-bar/GridSearchBar";
import CommonSelect, { CommonSelectItem } from "../../../common/components/select/CommonSelect";
import styles from "./customerPage.module.scss";
import CommonDatePicker from "../../../common/components/date-picker/CommonDatePicker";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import { useLazyProviderGetOrdersQuery, useUpdateOrderStatusMutation } from "../../../service/order-service";
import { toast } from "react-toastify";
import { SolarProviderCustomerQuery } from "../../../model/provider/provider-dashboard-data";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import { formatDate } from "../../../utils";
import { SolarOrder, OrderStatus, WorkflowEvent } from "../../../model/customer/customer";
import OrderDetailsSideDialog from "../components/OrderDetailsSideDialog/OrderDetailsSideDialog";

const ProviderCustomers = () => {
	const dispatch = useDispatch();
	const filterDictinary: any = useMemo(() => {
		return {
			status: ["In Progress", "Procesed", "Delivery In Progress", "Delivered", "Installed", "Retrieved", "Under Maintenance"],
		};
	}, []);

	const [selectedFilter, setSelectedFilter] = useState(Object.keys(filterDictinary)[0]);
	const [isPopUpOpen, setIsPopUpOpen] = useState(false);
	const [filters, setFilters] = useState<any>({});
	const [openDialog, setOpenDialog] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [pageQuery, setPageQuery] = useState<SolarProviderCustomerQuery>({
		page: 1,
		pageSize: 30,
	});
	const [selectedOrder, setSelectedOrder] = useState<SolarOrder>();

	const [getOrders, { data }] = useLazyProviderGetOrdersQuery();

	const [updateStatus, { isLoading }] = useUpdateOrderStatusMutation();

	useEffect(() => {
		handleDataFetch();
	}, []);


	useEffect(() => {
		handleDataFetch();
	}, [pageQuery?.paymentDate, pageQuery?.page, pageQuery?.search, pageQuery?.status]);

	const onRemoveFilter = (filterItem: any) => {
		setFilters((prev: any) => delete prev[filterItem]);
		setPageQuery({...pageQuery, status: ''})
	};

	const setFilter = (selectedFilterValue: string) => {
		setFilters((prev: any) => {
			return {
				...prev,
				[selectedFilter]: selectedFilterValue,
			};
		});
		setPageQuery({...pageQuery, status: selectedFilterValue})
		setIsPopUpOpen(prev => !prev);
	};

	function changeSearch(search: string) {
		setSearchText(search);
	}

	const popupFilterValues = useMemo(() => Object.keys(filterDictinary).map(x => new CommonSelectItem(x, x)), [filterDictinary]);

	const handleToggleDialog = () => {
		setOpenDialog(!openDialog);
	};

	const handleStatusUpdate = (orderId: string, newStatus: OrderStatus) => {
		updateStatus({ orderId: orderId, newStatus: newStatus })
			.unwrap()
			.then(() => {
				toast.success("Status updated successfully");
				handleToggleDialog();
			})
			.catch(err => toast.error(err?.data?.title));
	};

	const handleDataFetch = () => {
		dispatch(showLoader());
		getOrders(pageQuery)
			.unwrap()
			.catch(err => {
				toast.error("Unable to load customers");
			})
			.finally(() => {
				dispatch(hideLoader());
			});
	};

	const handleDate = (date: Date) => {
		setPageQuery({
			...pageQuery,
			paymentDate: date,
		});
	};

	const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			setPageQuery({ ...pageQuery, search: searchText });
		}
	};

	const getMostRecentEvent = (events: WorkflowEvent[]): WorkflowEvent | null => {
		if (!events || events.length === 0) {
		  return null;
		}
		
		const sortedEvents = [...events];
		
		sortedEvents.sort((a, b) => {
		  const dateA = new Date(a.timestamp);
		  const dateB = new Date(b.timestamp);
		  return dateB.getTime() - dateA.getTime();
		});
		
		return sortedEvents[0];
	  }

	return (
		<>
			{openDialog && selectedOrder && (
				<OrderDetailsSideDialog isReduxQuery order={selectedOrder} onCloseDialog={handleToggleDialog} onRefreshOrders={handleDataFetch} onUpdateOrderStatus={handleStatusUpdate} />
			)}
			<div>
				<div className={`${styles.filterSection}`}>
					<FilterBy filterDictionary={filterDictinary} onPopUpModal={() => setIsPopUpOpen(prev => !prev)} />
					<div className={styles.datePickerContainer}>
						<CommonDatePicker initialDate={new Date()} onChange={handleDate} />
					</div>
					<GridSearchBar
						placeholder={"Seach by name, email or phone number"}
						width={"320px"}
						onKeyDownHandler={handleEnter}
						onChangeHandler={searchText => changeSearch(searchText)}
					/>
				</div>
				{isPopUpOpen && (
					<div className={styles.modalPopup}>
						<div className={styles.modalProperties}>
							{
								<CommonSelect
									items={popupFilterValues}
									selected={popupFilterValues[0]}
									placeholder="Select Filter"
									onChange={item => setSelectedFilter(item?.key)}
									searchable={false}
								/>
							}
							<div className={styles.scrollDropdown}>
								{filterDictinary[selectedFilter].map((x: string) => (
									<div key={x} className={styles.propertyValue} onClick={() => setFilter(x)}>
										{x}
									</div>
								))}
							</div>
						</div>
					</div>
				)}
				{filters && (
					<div className={styles.filterSubSection}>
						{Object.keys(filters).map(x => (
							<div key={x} className={styles.filter}>
								{x}: <div className={styles.blackText}>{filters[x]}</div>
								<svg
									width="20px"
									height="20px"
									viewBox="0 -0.5 25 25"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									style={{ cursor: "pointer" }}
									onClick={() => onRemoveFilter(x)}
								>
									<path
										d="M6.96967 16.4697C6.67678 16.7626 6.67678 17.2374 6.96967 17.5303C7.26256 17.8232 7.73744 17.8232 8.03033 17.5303L6.96967 16.4697ZM13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697L13.0303 12.5303ZM11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303L11.9697 11.4697ZM18.0303 7.53033C18.3232 7.23744 18.3232 6.76256 18.0303 6.46967C17.7374 6.17678 17.2626 6.17678 16.9697 6.46967L18.0303 7.53033ZM13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303L13.0303 11.4697ZM16.9697 17.5303C17.2626 17.8232 17.7374 17.8232 18.0303 17.5303C18.3232 17.2374 18.3232 16.7626 18.0303 16.4697L16.9697 17.5303ZM11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697L11.9697 12.5303ZM8.03033 6.46967C7.73744 6.17678 7.26256 6.17678 6.96967 6.46967C6.67678 6.76256 6.67678 7.23744 6.96967 7.53033L8.03033 6.46967ZM8.03033 17.5303L13.0303 12.5303L11.9697 11.4697L6.96967 16.4697L8.03033 17.5303ZM13.0303 12.5303L18.0303 7.53033L16.9697 6.46967L11.9697 11.4697L13.0303 12.5303ZM11.9697 12.5303L16.9697 17.5303L18.0303 16.4697L13.0303 11.4697L11.9697 12.5303ZM13.0303 11.4697L8.03033 6.46967L6.96967 7.53033L11.9697 12.5303L13.0303 11.4697Z"
										fill="#000000"
									/>
								</svg>
							</div>
						))}
					</div>
				)}
			</div>
			<table className="standardTable">
				<thead>
					<tr>
						<th>CUSTOMER NAME</th>
						<th>PRODUCT ORDERED</th>
						<th>CREATED AT</th>
						<th>ORDER STATUS</th>
						<th>DATE STATUS CHANGED</th>
						<th>VIEW DETAILS</th>
					</tr>
				</thead>
				<tbody>
					{data?.items?.map(order => {
						return (
							<tr key={order?.details.id}>
								<td>{order?.details.contactInformation.fullName}</td>
								<td>{order?.details.productDetails.productName}</td>
								<td>{formatDate(order?.details?.createdAt ?? "")}</td>
								<td>{OrderStatus[order.details.statusDetails.status]}</td>
								<td>
									{formatDate(getMostRecentEvent(order.workflowDetails.events?? [])?.timestamp ?? "")}
								</td>
								<td>
									<button
										className={`actionButton`}
										onClick={() => {
											setSelectedOrder(order);
											handleToggleDialog();
										}}
									>
										VIEW DETAILS
									</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div className={styles.paginationCtn}>
				<CommonPagination
					numberOfPages={data?.totalPages ?? 0}
					currentPage={pageQuery?.page}
					onPageChange={pageNumber => setPageQuery({ ...pageQuery, page: pageNumber })}
				/>
			</div>
		</>
	);
};
export default ProviderCustomers;
